import React, { useEffect, useState } from 'react';
import {
  Row,
  Col,
  Select,
  Input,
  DatePicker,
  Form,
  Table,
  Typography,
  Space,
  Button,
  Modal,
  theme,
  Divider,
} from 'antd';
import {
  FilterOutlined,
  ReloadOutlined,
  ExclamationCircleFilled,
  CloudDownloadOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router';
import tradeBookService from '../../services/TradeBookService';
import { useAuth } from '../../hooks/useAuth';
import { v4 as uuidv4 } from 'uuid';
import OrderExecutionList from '../../components/Common/OrderExecutionList';
import { convertToLocal } from '../../components/Functions/convertToLocal';
import { objectToQueryString } from '../../util/Common';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const TradeBook = () => {
  // Form control
  const [form] = Form.useForm();
  const [requiredMark, setRequiredMarkType] = useState('');
  const onRequiredTypeChange = ({ requiredMarkValue }) => {
    setRequiredMarkType(requiredMarkValue);
  };
  const [orderList, setOrderList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [showForm, setShowForm] = useState(true);
  const [loading, setLoading] = useState(false);
  // const [page, setPage] = useState(1);
  // const [size, setSize] = useState(10);

  const navigate = useNavigate();
  const location = useLocation();
  // const [intervalTime, setIntervalTime] = useState(10000); // Default interval of 1 second
  const { showNotification } = useAuth();
  const { confirm } = Modal;

  const queryParams = new URLSearchParams(location.search);

  const startTimestamp = Number(queryParams.get('startDate')) || null;
  const endTimestamp = Number(queryParams.get('endDate')) || null;
  const tradingAccountNo = queryParams.get('tradingAccountNo') || null;
  const symbolCode = queryParams.get('symbolCode') || null;
  const orderSideType = queryParams.get('orderSideType') || null;
  const orderPriceType = queryParams.get('orderPriceType') || null;
  const orderExecutionStateType = queryParams.get('orderExecutionStateType') || null;
  const orderCancellationStateType = queryParams.get('orderCancellationStateType') || null;
  const orderExpiryStateType = queryParams.get('orderExpiryStateType') || null;

  const page = Number(queryParams.get('page')) || 1;
  const size = Number(queryParams.get('size')) || 10;

  const startDate = startTimestamp ? dayjs(startTimestamp * 1000) : null;
  const endDate = endTimestamp ? dayjs(endTimestamp * 1000) : null;

  // Table columns
  const tableColumns = [
    // {
    //   title: 'Trade Order ID',
    //   dataIndex: 'tradeOrderId',
    //   key: 'tradeOrderId',
    //   fixed: 'left',
    // },
    {
      title: 'Trading Account No.',
      dataIndex: 'tradingAccountNo',
      key: 'tradingAccountNo',
      fixed: 'left',
    },
    // {
    //   title: 'Cancellation Order ID',
    //   dataIndex: 'cancellationOrderId',
    //   key: 'cancellationOrderId',
    //   // removed duplicates first
    //   //   filters: [...new Set(applicationList.map(item => item.eyeColor))].map(item => {
    //   //     return {
    //   //       text: item,
    //   //       value: item,
    //   //     };
    //   //   }),
    //   //   onFilter: (value, record) => record.eyeColor.startsWith(value),
    // },
    {
      title: 'Security Code',
      dataIndex: 'securityCode',
      key: 'securityCode',
      fixed: 'left',
    },
    {
      title: 'Symbol Code',
      dataIndex: 'symbolCode',
      key: 'symbolCode',
    },
    {
      title: 'Order Price Type',
      dataIndex: 'orderPriceType',
      key: 'orderPriceType',
    },
    {
      title: 'Order Side Type',
      dataIndex: 'orderSideType',
      key: 'orderSideType',
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
      key: 'currency',
    },
    {
      title: 'Initiated Quantity',
      dataIndex: 'initiatedQuantity',
      key: 'initiatedQuantity',
      align: 'right',
      render: text => Number(text).toLocaleString('en-US'),
    },
    {
      title: 'Initiated Price',
      dataIndex: 'initiatedPrice',
      key: 'initiatedPrice',
      align: 'right',
      render: text => Number(text).toLocaleString('en-US'),
    },
    // {
    //   title: 'Pre Order',
    //   dataIndex: 'preOrder',
    //   key: 'preOrder',
    // },
    {
      title: 'Trade Window',
      dataIndex: 'tradeWindow',
      key: 'tradeWindow',
      align: 'right',
    },
    {
      title: 'Order Initiator Type',
      dataIndex: 'orderInitiatorType',
      key: 'orderInitiatorType',
      align: 'right',
    },
    {
      title: 'Order Initiator ID',
      dataIndex: 'orderInitiatorId',
      key: 'orderInitiatorId',
      align: 'right',
    },
    {
      title: 'Executed Quantity',
      dataIndex: 'executedQuantity',
      key: 'executedQuantity',
      align: 'right',
      render: text => Number(text).toLocaleString('en-US'),
    },
    {
      title: 'Order Execution State Type',
      dataIndex: 'orderExecutionStateType',
      key: 'orderExecutionStateType',
      align: 'right',
    },
    {
      title: 'Order Cancellation State Type',
      dataIndex: 'orderCancellationStateType',
      key: 'orderCancellationStateType',
      align: 'right',
      render: (text, record) => {
        return <Text>{record.orderCancellationStateType}</Text>;
      },
    },
    {
      title: 'Order Expiry State Type',
      dataIndex: 'orderExpiryStateType',
      key: 'orderExpiryStateType',
      align: 'right',
    },
    {
      title: 'Initiated At',
      dataIndex: 'initiatedAt',
      key: 'initiatedAt',
      align: 'right',
      render: (_, record) => convertToLocal(record.initiatedAt),
    },
    {
      title: 'Submitted At',
      dataIndex: 'submittedAt',
      key: 'submittedAt',
      align: 'right',
      render: (_, record) => convertToLocal(record.submittedAt),
    },
    {
      title: 'Accepted At',
      dataIndex: 'acceptedAt',
      key: 'acceptedAt',
      align: 'right',
      render: (_, record) => convertToLocal(record.acceptedAt),
    },
    {
      title: 'Fulfilled At',
      dataIndex: 'fulfilledAt',
      key: 'fulfilledAt',
      align: 'right',
      render: (_, record) => convertToLocal(record.fulfilledAt),
    },
    {
      title: 'Action',
      key: 'action',
      align: 'right',
      render: (_, record) => (
        <Space direction="horizontal" size={'middle'}>
          <Button
            // icon={
            //   <Image src={`${process.env.PUBLIC_URL}/images/delete-icon.png`} preview={false} />
            // }
            type={'default'}
            size="small"
            danger
            style={
              record.orderExecutionStateType === 'FULLY_FILLED' ||
              record.orderExpiryStateType === 'COMPLETED' ||
              record.orderExpiryStateType === 'EXPIRED'
                ? { display: 'none' }
                : { display: 'inline-block' }
            }
            onClick={() => showDeleteConfirm(record)}
          >
            Cancel
          </Button>
          <Button type={'primary'} size="small" onClick={() => handleView(record.tradeOrderId)}>
            View
          </Button>
        </Space>
      ),
    },
  ];

  const loadData = async () => {
    const payload = {
      tradingAccountNo: tradingAccountNo,
      startDate: startTimestamp,
      endDate: endTimestamp,
      symbolCode: symbolCode,
      orderSideType: orderSideType,
      orderPriceType: orderPriceType,
      orderExecutionStateType: orderExecutionStateType,
      orderCancellationStateType: orderCancellationStateType,
      orderExpiryStateType: orderExpiryStateType,
      page: page,
      size: size,
    };
    setLoading(true);

    const response = await tradeBookService.getTradeBook(payload);
    setLoading(false);

    if (response.success) {
      setOrderList(response.orderList);
      setTotalCount(response.totalCount);
    } else {
      showNotification('error', response.message);
    }
  };

  useEffect(() => {
    loadData();
  }, [location.search]);

  // useEffect(() => {
  //   // Schedule a refresh based on the selected interval
  //   const intervalId = setInterval(() => {
  //     loadData();
  //   }, intervalTime);

  //   // Clean up the interval when the component unmounts
  //   return () => clearInterval(intervalId);
  // }, [intervalTime]);

  const onFinish = formValues => {
    let startDateTimestamp = null;
    let endDateTimestamp = null;

    if (formValues.selectDates && formValues.selectDates[0]) {
      startDateTimestamp = formValues.selectDates[0].startOf('day').valueOf();
    }
    if (formValues.selectDates && formValues.selectDates[1]) {

      endDateTimestamp = formValues.selectDates[1].endOf('day').valueOf();
    }

    let payload = {
      tradingAccountNo: formValues.tradingAccountNumber ? formValues.tradingAccountNumber : null,
      startDate: startDateTimestamp !== null ? Math.floor(startDateTimestamp / 1000) : null,
      endDate: endDateTimestamp !== null ? Math.floor(endDateTimestamp / 1000) : null,
      symbolCode: formValues.symbolCode ? formValues.symbolCode : null,
      orderSideType: formValues.orderSideType ? formValues.orderSideType : null,
      orderPriceType: formValues.orderPriceType ? formValues.orderPriceType : null,
      orderExecutionStateType: formValues.orderExecutionStateType
        ? formValues.orderExecutionStateType
        : null,
      orderCancellationStateType: formValues.orderCancellationType
        ? formValues.orderCancellationType
        : null,
      orderExpiryStateType: formValues.orderExpiryType ? formValues.orderExpiryType : null,
      page: 1,
      size: 10,
    };

    const queryString = objectToQueryString(payload);
    navigate('/trading/trade-book?' + queryString);

    // form.resetFields();
  };

  // const handleIntervalChange = value => {
  //   setIntervalTime(value);
  // };

  const handleRefresh = () => {
    if (queryParams.size === 0) {
      loadData();
    } else {
      navigate('/trading/trade-book');
    }

    form.setFieldsValue({
      tradingAccountNumber: null,
      selectDates: [null, null],
      symbolCode: null,
      orderSideType: null,
      orderPriceType: null,
      orderExecutionStateType: null,
      orderCancellationType: null,
      orderExpiryType: null,
    });
  };

  const showDeleteConfirm = record => {
    confirm({
      title: 'Warning',
      icon: <ExclamationCircleFilled />,
      content: 'Are you sure to cancel this item?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        handleCancel(record);
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  };

  const handleCancel = async record => {
    const payload = {
      tradeOrderId: record.tradeOrderId,
    };
    const result = await tradeBookService.cancelOrder(payload);
    console.log(result);
    if (result.success) {
      showNotification('success', `Deleted Order ID - ${record.tradeOrderId}`);
      loadData();
    } else {
      showNotification('error', result.message);
    }
  };

  // primary theme
  const {
    token: { colorPrimary },
  } = theme.useToken();

  // show modal
  const [open, setOpen] = useState(false);
  const [tradeOrderId, setTradeOrderId] = useState();
  const handleView = tradeOrderId => {
    setOpen(true);
    setTradeOrderId(tradeOrderId);
  };

  return (
    <>
      {open && (
        <Modal
          title={
            <Title level={4} style={{ marginTop: 0, marginBottom: '1.5rem', color: colorPrimary }}>
              Order Execution List
            </Title>
          }
          open={open}
          centered
          width={1000}
          onCancel={() => setOpen(false)}
          footer={[]}
        >
          <OrderExecutionList tradeOrderId={tradeOrderId} />
        </Modal>
      )}
      <section
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Trade Book
        </Title>
        <Space>
          {/* <Select defaultValue={10000} onChange={handleIntervalChange}>
            <Select.Option value={10000}>10 second</Select.Option>
            <Select.Option value={30000}>30 seconds</Select.Option>
            <Select.Option value={50000}>50 seconds</Select.Option>
          </Select> */}
          <Button
            type="default"
            size={'middle'}
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
          ></Button>
          <Button
            type="default"
            size={'middle'}
            icon={<FilterOutlined />}
            onClick={() => setShowForm(showForm => (showForm = !showForm))}
          >
            Filter
          </Button>
        </Space>
      </section>
      <Divider />
      <Form
        onFinish={onFinish}
        form={form}
        labelCol={{
          span: 8,
          lg: {
            span: 12,
          },
          xl: {
            span: 12,
          },
        }}
        onValuesChange={onRequiredTypeChange}
        requiredMark={requiredMark}
        hidden={showForm}
        initialValues={{
          tradingAccountNumber: tradingAccountNo,
          selectDates: [startDate, endDate],
          symbolCode: symbolCode,
          orderSideType: orderSideType,
          orderPriceType: orderPriceType,
          orderExecutionStateType: orderExecutionStateType,
          orderCancellationType: orderCancellationStateType,
          orderExpiryType: orderExpiryStateType,
        }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={24} md={24} lg={12} xl={8}>
            <Form.Item label="Trading Account No" name={'tradingAccountNumber'}>
              <Input size="middle" />
            </Form.Item>

            <Form.Item label="Symbol Code" name={'symbolCode'}>
              <Input size="middle" />
            </Form.Item>
            <Form.Item label="Order Execution State Type" name={'orderExecutionStateType'}>
              <Select size="middle">
                <Select.Option value="PARTIALLY_FILLED">PARTIALLY_FILLED</Select.Option>
                <Select.Option value="FULLY_FILLED">FULLY_FILLED</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={24} md={24} lg={12} xl={8}>
            <Form.Item label="Order Side Type" name={'orderSideType'}>
              <Select size="middle">
                <Select.Option value="BUY">BUY</Select.Option>
                <Select.Option value="SELL">SELL</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item label="Order Price Type" name={'orderPriceType'}>
              <Select size="middle">
                <Select.Option value="MARKET">MARKET</Select.Option>
                <Select.Option value="LIMIT">LIMIT</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item label="Order Cancel Type" name={'orderCancellationType'}>
              <Select size="middle">
                <Select.Option value="NONE">NONE</Select.Option>
                <Select.Option value="REQUESTED">REQUESTED</Select.Option>
                <Select.Option value="COMPLETED">COMPLETED</Select.Option>
                <Select.Option value="REJECTED">REJECTED</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={24} lg={12} xl={8}>
            <Form.Item label="Order Expiry Type" name={'orderExpiryType'}>
              <Select size="middle">
                <Select.Option value="NONE">NONE</Select.Option>
                <Select.Option value="EXPIRED">EXPIRED</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item label="Select Dates" name={'selectDates'}>
              <RangePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Space style={{ float: 'right', margin: '20px 0px' }} align="center">
          <Button
            type="primary"
            size={'middle'}
            htmlType="submit"
            key="submit"
            icon={<SearchOutlined />}
          >
            Search
          </Button>
        </Space>
      </Form>

      <Table
        loading={loading}
        columns={tableColumns}
        dataSource={orderList?.map(order => ({ ...order, key: uuidv4() }))}
        size={'large'}
        scroll={{
          x: 'max-content',
        }}
        bordered
        pagination={{
          current: page,
          pageSize: size,
          total: totalCount,
        }}
        onChange={(pagination, filters, sorter) => {
          const formValues = {
            tradingAccountNo: tradingAccountNo,
            startDate: startTimestamp,
            endDate: endTimestamp,
            symbolCode: symbolCode,
            orderSideType: orderSideType,
            orderPriceType: orderPriceType,
            orderExecutionStateType: orderExecutionStateType,
            orderCancellationStateType: orderCancellationStateType,
            orderExpiryStateType: orderExpiryStateType,
            page: pagination.current,
            size: pagination.pageSize,
          };

          const queryString = objectToQueryString(formValues);
          navigate('/trading/trade-book?' + queryString);
        }}
      />
    </>
  );
};

export default TradeBook;
